// ============================================
// Bộ lọc màn góp ý
// ============================================
// Chỉ khai những bộ lọc BE THẬT SỰ đọc (`StaffFeedbackFilterParams`): trạng
// thái, loại, sao, khoảng ngày, và lối tắt `open`. CỐ Ý KHÔNG có ô tìm kiếm tự
// do — `/feedback/staff/` không nhận `search`, dựng một ô nhập mà gõ gì cũng
// không đổi kết quả là đúng kịch bản bug #159.

import { useMemo } from 'react';
import type { FeedbackRatingValue, FeedbackStatus, FeedbackType } from '@conai/shared-types';
import { SelectDropdown, type SelectOption } from '../../../components/shared';
import { cn } from '../../../utils/cn';
import {
  FEEDBACK_RATING_ORDER,
  FEEDBACK_STATUS_ORDER,
  FEEDBACK_TYPE_ORDER,
  useFeedbackText,
} from '../feedbackText';
import type { FeedbackFilterState } from '../filters';

interface FeedbackFilterBarProps {
  filters: FeedbackFilterState;
  /** `summary.by_status` — dict THƯA, mọi lần đọc phải `?? 0`. */
  statusCounts?: Partial<Record<FeedbackStatus, number>>;
  /** `summary.open` — BE đã tính sẵn, không tự cộng NEW + IN_REVIEW ở FE. */
  openCount?: number;
  /**
   * `/feedback/staff/summary/` hỏng -> ẨN HẲN badge đếm, không hiện `0`.
   *
   * `0` trên chip là một câu khẳng định: "không còn bản ghi nào ở trạng thái
   * này". Endpoint summary chết trong khi danh sách vẫn chạy thì câu đó sai, và
   * sai theo hướng khiến người dùng bỏ qua đúng những góp ý đang chờ xử lý.
   * Không biết thì không nói, chứ không nói bừa số 0.
   *
   * Chip vẫn bấm được: lọc là việc của `/feedback/staff/`, không dính gì tới
   * summary.
   */
  countsUnavailable?: boolean;
  onChange: (patch: Partial<FeedbackFilterState>) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

export function FeedbackFilterBar({
  filters,
  statusCounts,
  openCount,
  countsUnavailable = false,
  onChange,
  onReset,
  hasActiveFilters,
}: FeedbackFilterBarProps) {
  const { t, tr, statusLabel, typeLabel } = useFeedbackText();

  // Chỉ đếm khi CÓ số thật: summary lỗi, hoặc chưa về, thì không có badge nào.
  const showStatusCounts = !countsUnavailable && !!statusCounts;
  const showOpenCount = !countsUnavailable && typeof openCount === 'number';

  const typeOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: tr('feedbackAllTypes', 'All types') },
      ...FEEDBACK_TYPE_ORDER.map((type) => ({ value: type, label: typeLabel(type) })),
    ],
    [tr, typeLabel]
  );

  const ratingOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: tr('feedbackAllRatings', 'All ratings') },
      // Cao xuống thấp: người xử lý thường tìm 1-2 sao, để chúng gần đáy danh
      // sách quen mắt hơn là đảo ngược thang điểm.
      // Nhãn là "4 ★" chứ không phải "4 stars": số + ký hiệu thì ba ngôn ngữ đọc
      // như nhau, và tránh luôn chuyện số nhiều của tiếng Ba Lan/Ukraina (1 zirka
      // / 2 zirky / 5 zirok — ba dạng, `t()` ở đây không có plural rules).
      ...[...FEEDBACK_RATING_ORDER].reverse().map((star) => ({
        value: String(star),
        label: `${star} ★`,
      })),
    ],
    [tr]
  );

  /**
   * Bấm một trạng thái khi "Chỉ chưa xử lý" đang bật thì TẮT nút nhanh đó đi.
   *
   * Hai bộ lọc này loại trừ nhau ở tầng params (xem `buildFeedbackListParams`).
   * Nếu UI vẫn hiện cả hai cùng "đang bật" trong khi chỉ một cái có tác dụng thì
   * người dùng đọc màn hình ra một bộ lọc, còn bảng trả về một bộ lọc khác.
   */
  const toggleStatus = (status: FeedbackStatus) => {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter((item) => item !== status)
      : [...filters.statuses, status];
    onChange({ statuses: next, onlyOpen: false });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      {/* Hàng 1: lối tắt + chip trạng thái */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onChange({ onlyOpen: !filters.onlyOpen, statuses: [] })}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
            filters.onlyOpen
              ? 'border-amber-300 bg-amber-50 text-amber-700'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          )}
          aria-pressed={filters.onlyOpen}
        >
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              filters.onlyOpen ? 'bg-amber-500' : 'bg-slate-300'
            )}
          />
          {tr('feedbackOnlyOpen', 'Awaiting action only')}
          {showOpenCount ? (
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-xs font-bold',
                filters.onlyOpen ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
              )}
            >
              {openCount}
            </span>
          ) : null}
        </button>

        <span className="mx-1 hidden h-5 w-px bg-slate-200 sm:block" />

        {FEEDBACK_STATUS_ORDER.map((status) => {
          const active = !filters.onlyOpen && filters.statuses.includes(status);
          return (
            <button
              key={status}
              type="button"
              onClick={() => toggleStatus(status)}
              aria-pressed={active}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'border-primary-500 bg-primary-50 text-primary-600'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                // Mờ đi khi lối tắt đang chiếm quyền, nhưng KHÔNG `disabled`:
                // bấm vào đây là cách tự nhiên nhất để rời khỏi lối tắt đó.
                filters.onlyOpen && 'opacity-50'
              )}
            >
              {statusLabel(status)}
              {showStatusCounts ? (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-bold text-slate-500">
                  {/* Dict THƯA: trạng thái không có bản ghi nào thì BE không phát
                      khoá — `?? 0` chứ không phải `undefined` in ra màn hình.
                      `?? 0` ở đây là AN TOÀN vì đã có `showStatusCounts`: số 0
                      này đến từ một response thành công, nó nói "thật sự không
                      có bản ghi nào", chứ không phải "chưa biết". */}
                  {statusCounts?.[status] ?? 0}
                </span>
              ) : null}
            </button>
          );
        })}

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={onReset}
            className="ml-auto rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
          >
            {t('clearFilters')}
          </button>
        ) : null}
      </div>

      {filters.onlyOpen ? (
        <p className="mt-2 text-xs text-amber-600">
          {tr(
            'feedbackOpenOverridesStatus',
            '"Awaiting action" already means New + In review, so the status filter is paused. Pick a status to switch back.'
          )}
        </p>
      ) : null}

      {/* Hàng 2: loại / sao / khoảng ngày */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SelectDropdown
          size="sm"
          label={t('type')}
          options={typeOptions}
          value={filters.feedbackType}
          onChange={(value) => onChange({ feedbackType: (value || '') as FeedbackType | '' })}
        />

        <SelectDropdown
          size="sm"
          label={tr('feedbackRating', 'Rating')}
          options={ratingOptions}
          value={filters.rating === '' ? '' : String(filters.rating)}
          onChange={(value) =>
            onChange({ rating: value ? (Number(value) as FeedbackRatingValue) : '' })
          }
        />

        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-700">
            {tr('feedbackDateFrom', 'From date')}
          </label>
          <input
            type="date"
            value={filters.dateFrom}
            // `date_from`/`date_to` là `YYYY-MM-DD`, KHÔNG phải ISO datetime —
            // đúng bằng giá trị `<input type="date">` nhả ra, nên không cần (và
            // không được) chuyển qua `new Date().toISOString()`.
            max={filters.dateTo || undefined}
            onChange={(event) => onChange({ dateFrom: event.target.value })}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-900 shadow-sm outline-none focus:border-primary-500"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-700">
            {tr('feedbackDateTo', 'To date')}
          </label>
          <input
            type="date"
            value={filters.dateTo}
            min={filters.dateFrom || undefined}
            onChange={(event) => onChange({ dateTo: event.target.value })}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-900 shadow-sm outline-none focus:border-primary-500"
          />
        </div>
      </div>
    </div>
  );
}
